import { useEffect, useLayoutEffect, useRef, type PointerEvent as ReactPointerEvent, type RefObject } from 'react'

export type RubricDragItem = { kind: 'criterion' | 'section'; id: string }
export type RubricDropTarget = { sectionId: string; criterionId?: string; edge: 'before' | 'after' }

type Box = { top: number; bottom: number; left: number; right: number }
type SectionBox = Box & { id: string; element: HTMLElement; heading: Box; cards: (Box & { id: string; element: HTMLElement })[] }
type Session = {
  item: RubricDragItem; grip: HTMLElement; source: HTMLElement; pointerId: number
  startX: number; startY: number; x: number; y: number; offsetX: number; offsetY: number
  preview?: HTMLElement; width: number; frame: number; rootBox?: Box; sections: SectionBox[]
  target?: RubricDropTarget; marker?: HTMLElement
}

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
const settle = { duration: 220, easing: 'cubic-bezier(.2, .8, .2, 1)' }
const keyOf = (element: HTMLElement) => element.dataset.criterionId ? `criterion:${element.dataset.criterionId}` : `section:${element.dataset.sectionId}`
const movable = (root: HTMLElement) => [...root.querySelectorAll<HTMLElement>('[data-section-id], [data-criterion-id]')].filter(el => el.getClientRects().length)

/** Slides cards and sections from their previous positions after any structural change. */
export function useRubricFlip(root: RefObject<HTMLDivElement>, deps: unknown[]) {
  const before = useRef<{ rects: Map<string, DOMRect>; lifted?: string } | null>(null)
  useLayoutEffect(() => {
    const pending = before.current
    before.current = null
    if (!pending || !root.current || reducedMotion()) return
    const shifts = new Map<Element, [number, number]>()
    for (const element of movable(root.current)) {
      const previous = pending.rects.get(keyOf(element))
      if (!previous) continue
      const now = element.getBoundingClientRect()
      const shift: [number, number] = [previous.left - now.left, previous.top - now.top]
      shifts.set(element, shift)
      // A card inside a moving section already travels with it.
      const parent = shifts.get(element.parentElement?.closest('[data-section-id]') as Element)
      const [dx, dy] = parent ? [shift[0] - parent[0], shift[1] - parent[1]] : shift
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue
      const lifted = keyOf(element) === pending.lifted
      element.animate([
        { transform: `translate(${dx}px, ${dy}px)`, ...(lifted ? { zIndex: 20, boxShadow: '0 14px 32px rgb(0 0 0 / .16)' } : {}) },
        { transform: 'none', ...(lifted ? { zIndex: 20 } : {}) },
      ], lifted ? { ...settle, duration: 260 } : settle)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
  return (lifted?: { key: string; rect: DOMRect }) => {
    if (!root.current) return
    const rects = new Map(movable(root.current).map(element => [keyOf(element), element.getBoundingClientRect()]))
    if (lifted) rects.set(lifted.key, lifted.rect)
    before.current = { rects, lifted: lifted?.key }
  }
}

const box = (element: HTMLElement): Box => {
  const rect = element.getBoundingClientRect()
  return { top: rect.top + window.scrollY, bottom: rect.bottom + window.scrollY, left: rect.left, right: rect.right }
}

/** Pointer capture and one animation frame loop keep dragging independent of React renders. */
export function useRubricDrag(root: RefObject<HTMLDivElement>, onDrop: (item: RubricDragItem, target: RubricDropTarget, from: DOMRect) => boolean) {
  const session = useRef<Session | null>(null)
  const applyDrop = useRef(onDrop)
  useLayoutEffect(() => { applyDrop.current = onDrop }, [onDrop])

  useEffect(() => {
    const clearMarker = (drag: Session) => {
      drag.marker?.removeAttribute('data-drop-edge')
      drag.marker?.removeAttribute('data-drop-kind')
      drag.marker = undefined
      drag.target = undefined
    }
    const finish = (commit: boolean) => {
      const drag = session.current
      if (!drag) return
      session.current = null
      cancelAnimationFrame(drag.frame)
      root.current?.classList.remove('rubric-is-dragging')
      if (drag.grip.hasPointerCapture(drag.pointerId)) drag.grip.releasePointerCapture(drag.pointerId)
      const destination = drag.target
      clearMarker(drag)
      const preview = drag.preview
      if (!preview) return
      const release = () => { preview.remove(); drag.source.removeAttribute('data-dragging') }
      // A placed item slides from the preview into its slot; anything else returns home.
      if (commit && destination && applyDrop.current(drag.item, destination, preview.getBoundingClientRect())) { release(); return }
      const home = (drag.item.kind === 'section' ? drag.source.querySelector<HTMLElement>('.rubric-section-heading')! : drag.source).getBoundingClientRect()
      if (reducedMotion() || !home.height) { release(); return }
      preview.animate([{ transform: preview.style.transform, opacity: 1 }, { transform: `translate3d(${home.left}px, ${home.top}px, 0)`, scale: 1, opacity: .6 }], settle)
        .finished.then(release, release)
    }
    const mark = (drag: Session, destination?: RubricDropTarget, element?: HTMLElement) => {
      if (drag.target?.sectionId === destination?.sectionId && drag.target?.criterionId === destination?.criterionId && drag.target?.edge === destination?.edge) return
      clearMarker(drag)
      drag.target = destination
      drag.marker = element
      if (destination && element) {
        element.dataset.dropEdge = destination.edge
        element.dataset.dropKind = drag.item.kind
      }
    }
    const locate = (drag: Session) => {
      const y = drag.y + window.scrollY
      const area = drag.rootBox!
      if (drag.x < area.left - 16 || drag.x > area.right + 16 || y < area.top || y > area.bottom + 20) { mark(drag); return }
      const section = drag.sections.find(s => y >= s.top && y <= s.bottom)
        ?? drag.sections.reduce((nearest, s) => Math.min(Math.abs(y - s.top), Math.abs(y - s.bottom)) < Math.min(Math.abs(y - nearest.top), Math.abs(y - nearest.bottom)) ? s : nearest)
      if (!section) { mark(drag); return }
      if (drag.item.kind === 'section') {
        if (section.id === drag.item.id) { mark(drag); return }
        const movingDown = drag.sections.findIndex(s => s.id === drag.item.id) < drag.sections.indexOf(section)
        const edge = y < section.heading.top ? 'before' : y <= section.heading.bottom ? (movingDown ? 'after' : 'before') : 'after'
        mark(drag, { sectionId: section.id, edge }, section.element)
      } else {
        const card = section.cards.find(c => y >= c.top && y <= c.bottom)
        if (card?.id === drag.item.id) { mark(drag); return }
        // A gap belongs before its next card, including in a headingless section.
        const anchor = card ?? section.cards.find((candidate, index) =>
          y < candidate.top && y > (index === 0 ? section.heading.bottom : section.cards[index - 1].bottom))
        const edge = anchor && y < (anchor.top + anchor.bottom) / 2 ? 'before' : 'after'
        mark(drag, { sectionId: section.id, criterionId: anchor?.id, edge }, anchor?.element ?? section.element)
      }
    }
    const animate = () => {
      const drag = session.current
      if (!drag?.preview) return
      const left = Math.max(8, Math.min(window.innerWidth - drag.width - 8, drag.x - drag.offsetX))
      drag.preview.style.transform = `translate3d(${left}px, ${drag.y - drag.offsetY}px, 0)`
      const area = drag.rootBox!
      if (drag.x >= area.left && drag.x <= area.right) {
        const speed = drag.y < 90 ? -Math.min(18, (90 - drag.y) / 4) : drag.y > window.innerHeight - 80 ? Math.min(18, (drag.y - window.innerHeight + 80) / 4) : 0
        if (speed) window.scrollBy(0, speed)
      }
      locate(drag)
      drag.frame = requestAnimationFrame(animate)
    }
    const lift = (drag: Session) => {
      // A compact chip keeps the insertion line and the cards around it visible.
      const preview = document.createElement('div')
      const label = document.createElement('span')
      const detail = document.createElement('span')
      if (drag.item.kind === 'section') {
        label.textContent = drag.source.getAttribute('aria-label')
        detail.textContent = drag.source.querySelector('.rubric-section-count')?.textContent ?? ''
      } else {
        label.textContent = drag.item.id
        detail.textContent = drag.source.querySelector('textarea')?.value || 'New criterion'
      }
      preview.className = `rubric-drag-preview rubric-drag-${drag.item.kind}`
      preview.append(label, detail)
      preview.setAttribute('aria-hidden', 'true')
      drag.width = Math.min(420, drag.source.getBoundingClientRect().width)
      preview.style.width = `${drag.width}px`
      drag.offsetX = 18; drag.offsetY = 20
      drag.rootBox = box(root.current!)
      // Geometry is measured once. Scrolling only changes the pointer's document Y;
      // moving the preview and insertion marker never lays out the rubric again.
      drag.sections = [...root.current!.querySelectorAll<HTMLElement>('[data-section-id]')].map(element => ({
        ...box(element), id: element.dataset.sectionId!, element,
        heading: box(element.querySelector<HTMLElement>('.rubric-section-heading') ?? element),
        cards: [...element.querySelectorAll<HTMLElement>('[data-criterion-id]')].filter(card => card.getClientRects().length).map(card => ({ ...box(card), id: card.dataset.criterionId!, element: card })),
      }))
      root.current!.appendChild(preview)
      drag.preview = preview
      drag.source.dataset.dragging = 'true'
      root.current!.classList.add('rubric-is-dragging')
      animate()
    }
    const move = (e: PointerEvent) => {
      const drag = session.current
      if (!drag || e.pointerId !== drag.pointerId) return
      e.preventDefault()
      drag.x = e.clientX; drag.y = e.clientY
      if (!drag.preview && Math.hypot(drag.x - drag.startX, drag.y - drag.startY) >= 5) lift(drag)
    }
    const up = (e: PointerEvent) => {
      const drag = session.current
      if (!drag || e.pointerId !== drag.pointerId) return
      drag.x = e.clientX; drag.y = e.clientY
      if (drag.preview) locate(drag)
      finish(true)
    }
    const cancel = () => finish(false)
    const escape = (e: KeyboardEvent) => { if (e.key === 'Escape' && session.current) { e.preventDefault(); finish(false) } }
    document.addEventListener('pointermove', move, { passive: false })
    document.addEventListener('pointerup', up)
    document.addEventListener('pointercancel', cancel)
    document.addEventListener('keydown', escape)
    window.addEventListener('blur', cancel)
    window.addEventListener('resize', cancel)
    return () => {
      finish(false)
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', up)
      document.removeEventListener('pointercancel', cancel)
      document.removeEventListener('keydown', escape)
      window.removeEventListener('blur', cancel)
      window.removeEventListener('resize', cancel)
    }
  }, [root])

  return (e: ReactPointerEvent<HTMLElement>, item: RubricDragItem) => {
    if (e.button !== 0 || !e.isPrimary || session.current) return
    const source = e.currentTarget.closest<HTMLElement>(item.kind === 'criterion' ? '[data-criterion-id]' : '[data-section-id]')
    if (!source) return
    const bounds = (item.kind === 'section' ? source.querySelector<HTMLElement>('.rubric-section-heading')! : source).getBoundingClientRect()
    e.preventDefault(); e.stopPropagation()
    e.currentTarget.setPointerCapture(e.pointerId)
    session.current = { item, source, grip: e.currentTarget, pointerId: e.pointerId,
      startX: e.clientX, startY: e.clientY, x: e.clientX, y: e.clientY, offsetX: e.clientX - bounds.left, offsetY: e.clientY - bounds.top, width: 0, frame: 0, sections: [] }
  }
}
